import React from "react"
import styled from "styled-components"
import { rem } from "polished"

import { media } from "@sr/common"
import {
  IconChevronRight,
  SanityImg,
  SemiBdTitle3,
  SemiBdTitle4,
} from "@sr/gatsby-theme-ui"
import { Link } from "gatsby"
import SanityImage from "gatsby-plugin-sanity-image"

export type HardwareCompatibleHardwareCategoryShape = {
  cardTitle: string
  icon: SanityImg
  path: {
    current: string
  }
}

export interface HardwareCompatibleHardwarePropsShape {
  bgColor?: string
  hardwareCategories: HardwareCompatibleHardwareCategoryShape[]
  path?: string
  showHardwareLink?: boolean
}

const HardwareCompatibleHardware = ({
  bgColor = "var(--color-white)",
  hardwareCategories,
  path,
  showHardwareLink = true,
}: HardwareCompatibleHardwarePropsShape) => {
  const filteredCategories = path
    ? hardwareCategories.filter((category) => {
        return category.path.current !== path
      })
    : hardwareCategories

  return (
    <HardwareCompatibleHardwareStyled bgColor={bgColor}>
      <SemiBdTitle3>See more compatible hardware</SemiBdTitle3>
      <div className="hardware-compatible-hardware__cards-container">
        {filteredCategories.map((category, i) => {
          return (
            <Link
              to={category?.path?.current}
              key={i}
              className="hardware-compatible-hardware__card-link"
            >
              <div className="hardware-compatible-hardware__card-container">
                <div className="hardware-compatible-hardware__card-img-container">
                  {category?.icon?.asset && (
                    <SanityImage
                      asset={category?.icon?.asset}
                      alt={category?.icon?.alt || ""}
                      className="hardware-compatible-hardware__card-img"
                      crop={category?.icon?.crop}
                      hotspot={category?.icon?.hotspot}
                    />
                  )}
                </div>
                <div className="hardware-compatible-hardware__card-btn-container">
                  <p>{category?.cardTitle}</p>
                </div>
              </div>
            </Link>
          )
        })}
      </div>
      {showHardwareLink && (
        <div className="hardware-compatible-hardware__link-container">
          <Link to="/hardware/" className="hardware-compatible-hardware__link">
            <div className="hardware-compatible-hardware__link-icon-container">
              <IconChevronRight color="var(--color-primary)" />
            </div>
            <SemiBdTitle4 className="hardware-compatible-hardware__link-text">
              Explore all hardware
            </SemiBdTitle4>
          </Link>
        </div>
      )}
    </HardwareCompatibleHardwareStyled>
  )
}

export default HardwareCompatibleHardware

interface HardwareCompatibleHardwareSC {
  bgColor: string
}

const HardwareCompatibleHardwareStyled = styled.section<HardwareCompatibleHardwareSC>`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  padding: ${rem(40)} ${rem(24)} ${rem(32)};
  background: ${({ bgColor }) => bgColor};

  @media ${media.midUp} {
    padding: ${rem(80)} ${rem(140)} ${rem(64)};
  }

  @media ${media.stMidUp} {
    padding: ${rem(104)} 0;
  }

  h3 {
    font-size: ${rem(32)};
    line-height: ${rem(40)};

    @media ${media.midUp} {
      font-size: ${rem(40)};
      line-height: ${rem(48)};
      max-width: ${rem(509)};
    }
  }

  .hardware-compatible-hardware__cards-container {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: ${rem(16)};
    padding-top: ${rem(32)};

    @media ${media.midUp} {
      gap: ${rem(32)};
      padding-top: ${rem(48)};
    }

    @media ${media.stMidUp} {
      gap: ${rem(40)} ${rem(32)};
    }

    @media ${media.stMidUp} {
      padding-top: ${rem(56)};
      max-width: ${rem(960)};
    }

    .hardware-compatible-hardware__card-link {
      text-decoration: none;

      .hardware-compatible-hardware__card-container {
        overflow: hidden;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        width: ${rem(156)};
        height: ${rem(160)};
        border-radius: ${rem(8)};
        box-shadow: 0 ${rem(4)} ${rem(8)} rgba(45, 50, 57, 0.2);
        background: var(--color-white);

        @media ${media.midUp} {
          width: ${rem(216)};
          height: ${rem(216)};
        }

        .hardware-compatible-hardware__card-img-container {
          width: ${rem(156)};
          height: ${rem(120)};
          display: flex;
          justify-content: center;
          align-items: center;

          @media ${media.midUp} {
            width: ${rem(216)};
            height: ${rem(168)};
          }

          img {
            @media ${media.midUp} {
              width: ${rem(110)};
              height: ${rem(110)};
            }
          }
        }

        .hardware-compatible-hardware__card-btn-container {
          width: 100%;
          height: ${rem(40)};
          display: flex;
          justify-content: center;
          align-items: center;
          background: var(--color-primary);
          margin-top: auto;

          @media ${media.midUp} {
            height: ${rem(48)};
          }

          p {
            color: var(--color-white);
            font-size: ${rem(12)};
            font-weight: 600;

            @media ${media.midUp} {
              font-size: ${rem(16)};
            }
          }
        }
      }
    }
  }

  .hardware-compatible-hardware__link-container {
    display: flex;
    justify-content: center;
    align-items: center;
    padding-top: ${rem(24)};

    @media ${media.midUp} {
      padding-top: ${rem(56)};
    }

    @media ${media.stMidUp} {
      padding-top: ${rem(48)};
    }

    .hardware-compatible-hardware__link {
      display: flex;
      justify-content: center;
      align-items: center;
      gap: ${rem(8)};
      color: var(--color-primary);
      text-decoration: none;
    }

    .hardware-compatible-hardware__link-icon-container {
      display: flex;
      justify-content: center;
      align-items: center;
      width: ${rem(32)};
      height: ${rem(32)};
      border: ${rem(1)} solid var(--color-primary);
      border-radius: ${rem(32)};
    }

    .hardware-compatible-hardware__link-text {
      padding: 0;

      @media ${media.stMidUp} {
        font-size: ${rem(20)};
      }
    }
  }
`
